import { pickLocale, type PackageCharacteristic } from '../../schema';
import { useComponent, useDisplayPrice, useValuePriceFormatter } from '../../state/ComponentContext';
import { useCharacteristicSelection, useConfigentoStoreRef } from '../../state/StoreContext';
import { useTranslation } from '../../i18n';
import { applyPackage } from './applyPackage';

interface PackageViewProps {
    characteristic: PackageCharacteristic;
    locale: string | undefined;
}

/**
 * Interactive package toggle. Bound semantically to the legacy
 * Justselling_Configento_Option_Type_Package: a binary on/off widget
 * that, when activated, cascades preselected values into a list of
 * referenced sub-Characteristics ({@link PackageCharacteristic#packageValues}).
 *
 * The package's own selection state lives in the store as a
 * `{ kind: 'single', valueId: 1 }` sentinel — the value-id doesn't
 * carry semantic meaning, only the entry's presence does. Restore
 * round-trips through that shape unchanged.
 *
 * Cascading happens in {@link applyPackage} — separate module so the
 * (target-Characteristic, store-action) mapping can be reused by
 * future host code that wants to apply a package programmatically.
 */
export function PackageView({ characteristic, locale }: PackageViewProps) {
    const { t } = useTranslation();
    const component = useComponent();
    const store = useConfigentoStoreRef();
    const selection = useCharacteristicSelection(characteristic.id);
    const displayPrice = useDisplayPrice();
    const formatValuePrice = useValuePriceFormatter();

    const active = selection?.kind === 'single' && selection.valueId !== null;
    const packagePriceSuffix = formatValuePrice(characteristic.packagePrice);
    const hasValues = characteristic.values.length > 0;
    const inputId = `cfg-package-${characteristic.id}`;
    const label =
        characteristic.values[0]
            ? (pickLocale(characteristic.values[0].title, locale)
                ?? characteristic.values[0].value
                ?? t('package.activate'))
            : t('package.activate');

    return (
        <div className="cfg-package" data-configento-active={active || undefined}>
            <label className="cfg-package__toggle" htmlFor={inputId}>
                <input
                    id={inputId}
                    type="checkbox"
                    checked={active}
                    onChange={(e) => applyPackage(component, characteristic, store, e.target.checked)}
                />
                <span className="cfg-package__label">{label}</span>
                {displayPrice && packagePriceSuffix && (
                    <span className="cfg-package__price">{packagePriceSuffix}</span>
                )}
            </label>

            {/* Transparency: if the package has its own values list, show
                what the merchant is opting into. Useful when the package
                bundles multiple options across other Characteristics —
                listing them here means the customer doesn't have to scan
                the whole configurator to see what changed. */}
            {hasValues && characteristic.values.length > 1 && (
                <ul className="cfg-package__values">
                    {characteristic.values.map((value) => {
                        const lbl = pickLocale(value.title, locale) ?? value.value ?? `#${value.id}`;
                        const priceSuffix = formatValuePrice(value.price);
                        return (
                            <li
                                key={value.id}
                                className="cfg-package__value"
                                data-configento-value-id={value.id}
                            >
                                <span>{lbl}</span>
                                {priceSuffix && (
                                    <span className="cfg-toggle-item__price">{priceSuffix}</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* When the package has refs but no own values, surface the
                count so the merchant sees this is a cascade-only widget
                (legacy templates rendered the bundled chars inline; V3
                shows them in their natural positions across the grid). */}
            {!hasValues && characteristic.packageValues.length > 0 && (
                <p className="cfg-char__info">
                    {t('package.value_refs', { n: characteristic.packageValues.length })}
                </p>
            )}
            {!hasValues && characteristic.packageValues.length === 0 && (
                <p className="cfg-char__info">{t('package.empty')}</p>
            )}
        </div>
    );
}
